import React from 'react';
import dayjs from 'dayjs';
import {
  Check,
  CreditCard,
  Eye,
  FileSearch,
  Mail,
  Receipt,
  Send,
  ShoppingCart,
  Tournament,
  X
} from 'tabler-icons-react';
import roundHalfEven from 'round-half-even';
import { formatUtcDate } from './format';
import { getOrderSummaryInfo, translateStoreOrderData } from './storeHelper';
import {
  PAYMENT_PROVIDER_LINE_ITEM_TYPES,
  PAYMENT_TYPE_ENUM,
  REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM_VALUES,
  REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM,
  REG_FORM_SUBMISSION_STATUS_ENUM,
  SERVICE_TYPE_ENUM,
  WEBSITE_PAYMENT_STATUS_ENUM
} from '../config/constants';

const REG_FORM_SUBMISSION_VIEW_ACTIONS = [
  {
    label: 'Detailed View',
    value: 'details',
    section: 1,
    icon: <Eye size={18} />,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) => !isOrderDetails && !isSubmissionDetails && !isSummaryView
  },
  {
    label: 'Order Details',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.orderSummary,
    section: 1,
    icon: <ShoppingCart size={18} />,
    getLink: (data) =>
      `/admin/financials/orders/${data.fkRegCart}?from=registrations${
        data.isTableView ? '' : `&fromId=${data.pkRegFormSubmission}`
      }`,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) => !isOrderDetails && !isSummaryView
  },
  {
    label: 'View Form',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.view,
    section: 1,
    icon: <FileSearch size={18} />,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) => !isSummaryView
  },
  {
    label: 'Resend receipt',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.resendReceipt,
    section: 2,
    icon: <Send size={18} />,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) => isSubmissionDetails
  },
  {
    label: 'Email Contact',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.emailUser,
    section: 2,
    icon: <Send size={18} />,
    getLink: (submission) =>
      `mailto:${submission.overrideEmail || submission.user.email}`,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) => !isOrderDetails && !isSummaryView
  },
  {
    label: 'Change Email',
    modalTitle: 'Change contact email',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.changeOverrideEmail,
    section: 2,
    icon: <Mail size={18} />,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) => !isSummaryView
  },
  {
    label: 'Change Division',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.changeDivision,
    section: 2,
    icon: <Tournament size={18} />,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) => !isOrderDetails && !isSummaryView
  },
  {
    label: 'Reinstate',
    modalTitle: 'Reinstate registration',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.reinstate,
    section: 2,
    icon: <Check size={18} />,
    isVisable: (
      submissions,
      { isAdmin, isOrderDetails, isSubmissionDetails, isSummaryView }
    ) =>
      !isSummaryView &&
      submissions.some(
        (s) =>
          s.fkRegFormSubmissionStatus.toString() ===
            REG_FORM_SUBMISSION_STATUS_ENUM.DENIED ||
          (isAdmin &&
            s.fkRegFormSubmissionStatus.toString() ===
              REG_FORM_SUBMISSION_STATUS_ENUM.REFUNDED)
      )
  },
  {
    label: 'Record Payment',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.viewPayments,
    section: 3,
    icon: <Receipt size={18} />,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) =>
      (isSummaryView && !isSubmissionDetails) ||
      submissions.some(
        (s) =>
          s.fkRegFormSubmissionStatus.toString() ===
            REG_FORM_SUBMISSION_STATUS_ENUM.PENDING ||
          s.fkRegFormSubmissionStatus.toString() ===
            REG_FORM_SUBMISSION_STATUS_ENUM.PENDING_PAYMENT
      )
  },
  {
    label: 'View Payments',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.viewPayments,
    section: 3,
    icon: <Receipt size={18} />,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) =>
      (!isSummaryView || isSubmissionDetails) &&
      submissions.every(
        (s) =>
          s.fkRegFormSubmissionStatus.toString() !==
            REG_FORM_SUBMISSION_STATUS_ENUM.PENDING &&
          s.fkRegFormSubmissionStatus.toString() !==
            REG_FORM_SUBMISSION_STATUS_ENUM.PENDING_PAYMENT
      )
  },
  {
    label: 'Invoice',
    modalTitle: 'Invoice registration',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.invoice,
    section: 3,
    icon: <CreditCard size={18} />
  },
  {
    label: 'Deny',
    modalTitle: 'Deny registration',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.deny,
    section: 4,
    color: '#c40000',
    icon: <X color="#c40000" size={18} />,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) =>
      (isSubmissionDetails || !isSummaryView) &&
      submissions.some(
        (s) =>
          s.fkRegFormSubmissionStatus.toString() ===
            REG_FORM_SUBMISSION_STATUS_ENUM.ACCEPTED ||
          s.fkRegFormSubmissionStatus.toString() ===
            REG_FORM_SUBMISSION_STATUS_ENUM.PENDING ||
          s.fkRegFormSubmissionStatus.toString() ===
            REG_FORM_SUBMISSION_STATUS_ENUM.PENDING_PAYMENT
      )
  },
  {
    label: 'Refund',
    value: REG_FORM_SUBMISSION_MODAL_ACTIONS_ENUM.refund,
    section: 4,
    color: '#c40000',
    icon: <CreditCard color="#c40000" size={18} />,
    isVisable: (
      submissions,
      { isOrderDetails, isSubmissionDetails, isSummaryView }
    ) => !isOrderDetails && (!isSummaryView || isSubmissionDetails)
  }
];

const getInelligibleRegPersonReason = (
  regPerson,
  divisionForm,
  externalInvite = null
) => {
  const { firstName, lastName, dob, gender } = regPerson;
  const { regAssociationDivision } = divisionForm || {};

  if (
    externalInvite &&
    `${externalInvite.participantFirstName}${externalInvite.participantLastName}`.toLowerCase() !==
      `${firstName}${lastName}`.toLowerCase()
  ) {
    return 'Invite does not match registrant';
  }

  const dobDate = new Date(formatUtcDate(dob));
  if (
    regAssociationDivision?.minDob &&
    dobDate > new Date(formatUtcDate(regAssociationDivision.maxDob))
  ) {
    return ` Too young for this program. Born after ${dayjs(
      formatUtcDate(regAssociationDivision.maxDob)
    ).format('MM/DD/YYYY')}.`;
  }

  if (
    regAssociationDivision?.maxDob &&
    dobDate < new Date(formatUtcDate(regAssociationDivision.minDob))
  ) {
    return `Too old for this program. Born before ${dayjs(
      formatUtcDate(regAssociationDivision.minDob)
    ).format('MM/DD/YYYY')}`;
  }

  if (regAssociationDivision?.fkRegGenderType === 2 && gender !== 'Female') {
    return `The division '${regAssociationDivision.name}' is a girls only division.`;
  }

  if (regAssociationDivision?.fkRegGenderType === 3 && gender !== 'Male') {
    return `The division '${regAssociationDivision.name}' is a boys only division.`;
  }

  return null;
};

const getExpiryDateInfo = (expiresAt) => {
  const expiryDate = formatUtcDate(expiresAt);
  const hasExpired = new Date() >= new Date(expiryDate);
  let expiryTimeHours = dayjs(expiryDate).diff(dayjs(new Date()), 'hour');
  const expiryTimeMinutes = Math.ceil(
    dayjs(expiryDate).diff(dayjs(new Date()), 'minute', true)
  );
  const expiryTimeDays = Math.ceil(expiryTimeHours / 24);
  expiryTimeHours -= Math.floor(expiryTimeHours / 24) * 24;

  return { hasExpired, expiryTimeDays, expiryTimeHours, expiryTimeMinutes };
};

const getDaysOfPlayString = (divisionDaysOfPlay = []) => {
  const daysOfPlay = {
    monday: 'Mon',
    tuesday: 'Tues',
    wednesday: 'Wed',
    thursday: 'Thurs',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun'
  };

  let daysOfPlayString = '';
  if (divisionDaysOfPlay.length === 0) {
    daysOfPlayString = 'Variable';
  }
  else {
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const weedends = ['saturday', 'sunday'];
    const daysOfWeekLowercase = divisionDaysOfPlay.map((d) => d.toLowerCase());
    const containsAllWeekdays = !weekdays.some(
      (d) => !daysOfWeekLowercase.includes(d)
    );
    const containsAllWeekends = !weedends.some(
      (d) => !daysOfWeekLowercase.includes(d)
    );

    if (containsAllWeekdays && containsAllWeekends) {
      daysOfPlayString = 'Variable';
    }
    else if (containsAllWeekdays) {
      daysOfPlayString = 'Weekdays';
    }
    else if (containsAllWeekends) {
      daysOfPlayString = 'Weekends';
    }
    else {
      daysOfPlayString = daysOfWeekLowercase
        .map((d) => daysOfPlay[d])
        .join(' / ');
    }
  }

  return daysOfPlayString;
};

const getRegFormSubmissionStatusInfo = (submission) =>
  !submission
    ? {}
    : submission.fkRegFormSubmissionStatus.toString() ===
      REG_FORM_SUBMISSION_STATUS_ENUM.ACCEPTED
    ? {
        color: 'green',
        label: 'ACCEPTED'
      }
    : submission.fkRegFormSubmissionStatus.toString() ===
      REG_FORM_SUBMISSION_STATUS_ENUM.DENIED
    ? {
        color: '#c40000',
        label: 'DENIED'
      }
    : submission.fkRegFormSubmissionStatus.toString() ===
      REG_FORM_SUBMISSION_STATUS_ENUM.REFUNDED
    ? {
        color: 'gray',
        label: 'REFUNDED'
      }
    : {
        color: 'blue',
        label: 'PENDING'
      };

const getRegCartOrderSummaryInfo = (d) => {
  const result = {
    completelyRefunded: false,
    isFullyCancelled: false,
    itemCount: 0,
    totalPaid: 0,
    totalRefunded: 0,
    totalInvoiceAmount: 0,
    paymentStatusInfo: {},
    statusInfo: {},
    purchasedAt: null
  };

  const formSubmissionInfo = d.regFormSubmissions.reduce(
    (r, c) => {
      const payments = c.regFormSubmissionPayments.reduce(
        (acc, p) => {
          switch (p.fkRegFormSubmissionPaymentType.toString()) {
            case PAYMENT_TYPE_ENUM.REFUND:
              acc.refunded += p.amount * 100;
              break;
            case PAYMENT_TYPE_ENUM.INVOICE:
              acc.invoiceAmount += p.amount * 100;
              break;
            default:
              acc.paid += p.amount * 100;
          }
          return acc;
        },
        {
          paid: 0,
          invoiceAmount: 0,
          refunded: 0,
          isFullyRefunded: false
        }
      );

      return {
        purchasedAt: r.purchasedAt || c.submittedAt,
        count: r.count + 1,
        totalPaid: r.totalPaid + payments.paid,
        totalRefunded: r.totalPaid + payments.refunded,
        totalInvoiceAmount: r.totalPaid + payments.invoiceAmount,
        isPendingPayment:
          r.isPendingPayment ||
          c.fkRegFormSubmissionStatus.toString() ===
            REG_FORM_SUBMISSION_STATUS_ENUM.PENDING_PAYMENT
      };
    },
    {
      purchasedAt: null,
      totalPaid: 0,
      totalRefunded: 0,
      totalInvoiceAmount: 0,
      count: 0,
      isPendingPayment: false
    }
  );

  const merchPackageInfo = d.regCartMerchPackages.reduce(
    (r, c) => {
      const payments = c.regCartMerchPackagePayments.reduce(
        (acc, p) => {
          switch (p.fkRegFormSubmissionPaymentType.toString()) {
            case PAYMENT_TYPE_ENUM.REFUND:
              acc.refunded += p.amount * 100;
              break;
            default:
              acc.paid += p.amount * 100;
          }
          return acc;
        },
        {
          paid: 0,
          invoiceAmount: 0,
          refunded: 0
        }
      );

      return {
        purchasedAt: r.purchasedAt || payments[0]?.createdAt,
        count: r.count + c.count,
        totalPaid: r.totalPaid + payments.paid,
        totalRefunded: r.totalPaid + payments.refunded
      };
    },
    {
      purchasedAt: null,
      totalPaid: 0,
      totalRefunded: 0,
      count: 0
    }
  );

  if (d.ecomOrder) {
    const {
      totals,
      completelyRefunded,
      bulkProducts,
      bulkInfo,
      productInfo,
      isFullyCancelled,
      totalPaid,
      minimumQuantity,
      statusInfo,
      paymentStatusInfo
    } = getOrderSummaryInfo(d.ecomOrder);

    result.itemCount = totalPaid;

    result.purchasedAt = d.ecomOrder.purchasedAt;
    result.totalPaid = totals.paid;
  }

  result.purchasedAt =
    result.purchasedAt ||
    formSubmissionInfo.purchasedAt ||
    merchPackageInfo.purchasedAt;
  result.itemCount += merchPackageInfo.count + formSubmissionInfo.count;
  result.totalPaid += merchPackageInfo.totalPaid + formSubmissionInfo.totalPaid;
  result.totalRefunded +=
    merchPackageInfo.totalRefunded + formSubmissionInfo.totalRefunded;
  result.totalInvoiceAmount += formSubmissionInfo.totalInvoiceAmount;

  const completelyRefunded =
    result.totalPaid > 0 && result.totalRefunded >= result.totalPaid;
  const { isPendingPayment } = formSubmissionInfo;

  result.paymentStatusInfo = isPendingPayment
    ? {
        color: 'blue',
        label: 'Pending payment'
      }
    : completelyRefunded
    ? {
        color: '#C40000',
        label: 'Refunded'
      }
    : result.totalRefunded > 0
    ? {
        color: '#666',
        label: 'Partially refunded'
      }
    : {
        color: 'dodgerblue',
        label: 'Paid'
      };

  return result;
};

const translateRegCartOrderFormSubmissionData = (lineItem, regCartProduct) => {
  const { regFormSubmission } = regCartProduct;

  const invoices = lineItem.invoices.filter(
    (i) =>
      i.isAdditionalCharge &&
      (i.invoiceStatus.toString() ===
        REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM_VALUES.PENDING ||
        i.invoiceStatus.toString() ===
          REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM_VALUES.PAID)
  );

  return {
    key: lineItem.entityId,
    to: `/registrations/${regFormSubmission.pkRegFormSubmission}`,
    lineItem,
    regCartProduct,
    fkRegFormSubmissionStatus: regFormSubmission.fkRegFormSubmissionStatus,
    regFormSubmission,
    invoices
  };
};

const translateRegCartOrderData = (regCart) => {
  const regFormSubmissions = regCart
    ? regCart.cart.lineItems
        .filter(
          (i) =>
            i.fkService.toString() === SERVICE_TYPE_ENUM.PLAYER_REG &&
            i.lineItemType ===
              PAYMENT_PROVIDER_LINE_ITEM_TYPES.registration.value
        )
        .map((i) =>
          translateRegCartOrderFormSubmissionData(
            i,
            regCart.regCartProducts.find(
              (c) =>
                c.regFormSubmission.pkRegFormSubmission === i.entityId &&
                c.regProduct.pkRegCartProduct ===
                  c.regFormSubmission.regAssociationDivisionForm.regProduct
                    .pkRegCartProduct
            ),
            regCart
          )
        )
        .sort(
          (a, b) =>
            a.regFormSubmission.regAssociationDivisionForm.regAssociationDivision.name.localeCompare(
              b.regFormSubmission.regAssociationDivisionForm
                .regAssociationDivision.name
            ) ||
            a.regFormSubmission.regPerson.firstName.localeCompare(
              b.regFormSubmission.regPerson.firstName
            ) ||
            a.regFormSubmission.regPerson.lastName.localeCompare(
              b.regFormSubmission.regPerson.lastName
            )
        )
    : [];

  const ecomOrder = translateStoreOrderData(regCart?.ecomOrder);

  const invoices = regFormSubmissions
    .flatMap((r) => r.invoices)
    .sort((a, b) => new Date(b.created) - new Date(a.created));

  const invoiceTotals = invoices.reduce(
    (r, c) => ({
      totalInCents: r.totalInCents + c.totalInCents,
      amountPaidInCents: r.amountPaidInCents + c.amountPaidInCents,
      balanceInCents:
        r.balanceInCents +
        (c.totalInCents - c.amountPaidInCents - c.forgivenessAmountInCents),
      forgivenessAmountInCents:
        r.forgivenessAmountInCents + c.forgivenessAmountInCents,
      refundedAmountInCents: r.refundedAmountInCents + c.refundedAmountInCents,
      serviceFeeInCents: r.serviceFeeInCents + c.serviceFeeInCents,
      amountDueInCents: r.amountDueInCents + c.amountDueInCents
    }),
    {
      totalInCents: 0,
      amountPaidInCents: 0,
      refundedAmountInCents: 0,
      balanceInCents: 0,
      forgivenessAmountInCents: 0,
      serviceFeeInCents: 0,
      amountDueInCents: 0
    }
  );

  const installments =
    regCart?.cart.payments.flatMap((p) => p.installments) ?? [];

  const pendingInstallments =
    installments
      .filter(
        (p) =>
          p.paymentStatus.toString() ===
            WEBSITE_PAYMENT_STATUS_ENUM.SCHEDULED ||
          p.paymentStatus.toString() === WEBSITE_PAYMENT_STATUS_ENUM.PENDING
      )
      .reduce((r, c) => {
        const existing = r.find((i) => i.dueDate === c.dueDate);
        if (existing) {
          existing.chargeAmount += c.chargeAmount;
        }
        else {
          r.push({ ...c });
        }
        return r;
      }, []) ?? [];

  return {
    regFormSubmissions,
    ecomOrder,
    invoices,
    invoiceTotals,
    installments,
    pendingInstallments
  };
};

export {
  REG_FORM_SUBMISSION_VIEW_ACTIONS,
  getInelligibleRegPersonReason,
  getExpiryDateInfo,
  getDaysOfPlayString,
  getRegFormSubmissionStatusInfo,
  getRegCartOrderSummaryInfo,
  translateRegCartOrderData
};
